import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { DesktopOnly } from './MobileOnly';
import ArtworkModal from './ArtworkModal';

// Styled component for the main container
const Container = styled.div`
  overflow: hidden;
  padding: 0.5rem;
  overflow-y: ${({ selectedArtwork }) => (selectedArtwork ? 'hidden' : 'auto')};
  position: relative; /* Add relative positioning */
  @media (max-width: 768px) {
    margin-top: 15rem;
    align-items: center; /* Center items vertically on mobile */
    flex-direction: column;
  }
`;

// Styled component for unclicked artwork card
const ArtworkCard = styled.div`
  position: relative;
  cursor: ${({ isMobile }) => (isMobile ? 'auto' : 'pointer')};
  overflow: hidden;
  width: 15%;
  object-fit: cover;  
  flex-grow: 2;
  color: rgb(var(--background-color));
  background-color: transparent;
  background-size: cover;
  background-position: center;
  transition: width 0.3s ease;
  z-index: 1;
  text-decoration: none;
  display: flex;
  align-items: center;
  top: 50%;
  height: 100vh;
  max-width: 30%;
  
  @media (max-width: 768px) {
    width: auto;
    height:auto;
    margin-top: 2rem;
    max-width: 90vw;
    max-height: 80vh;
    pointer-events: auto;
  }

  &:hover {
    ${({ isMobile }) =>
      !isMobile &&
      css`
        width: ${({ portrait }) => (portrait ? '200px' : '500px')};
        align-self: center;
        color: rgb(var(--background-color));
        background-color: rgb(var(--primary-color));
      `}
  }

  .active & {
    text-decoration: underline;
  }

  /* Additional styling for images */
  img video {
    width: auto;
    height: auto;
    object-fit: cover;  
    max-width: 100%;
    max-height: 30%;

    @media (max-width: 768px) {
      width: ${({ aspectRatio }) => (aspectRatio <= 1 ? 'auto' : '100%')};
      height: ${({ aspectRatio }) => (aspectRatio <= 1 ? '100%' : 'auto')};
    }
  }
`;

// Styled component for the artworks container
const ArtworksContainer = styled.div`
  display: flex;
  justify-content: center;
  transition: width 0.3s ease;
  align-items: center; /* Center items vertically */
  gap: 0.2rem;
  @media (max-width: 768px) {
    align-items: center; /* Center items vertically */
    flex-direction: column;
  }
`;

// Styled component for the artwork image
const ArtworkImage = styled.img`
  max-height: 100vh;
  max-width: 100%;
  background-color: transparent;
  width: auto;
  height: auto;
  @media (max-width: 768px) {
    width: ${({ portrait }) => (portrait ? 'auto' : '100%')};
    height: ${({ portrait }) => (portrait ? '100%' : 'auto')};
    pointer-events: auto;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

// Main Artworks Component
const Artworks = ({ category, maxColumns }) => {
  const [artworks, setArtworks] = useState([]);
  const [originalArtworks, setOriginalArtworks] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    fetch('/artworks.json')
      .then(response => response.json())
      .then(data => {
        setOriginalArtworks(data);
        filterArtworks(data, category);
      })
      .catch(error => console.error('Error fetching artworks:', error));
  }, [category]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const filterArtworks = (data, category) => {
    if (category === 'All') {
      setArtworks(data.slice().sort((a, b) => a.title.localeCompare(b.title)));
    } else if (category) {
      setArtworks(data.filter(artwork => artwork.category === category));
    }
  };

  const openModal = artwork => {
    setSelectedArtwork(artwork);
    setSelectedImageIndex(prevIndex => ({
      ...prevIndex,
      [artwork.id]: 0
    }));
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setSelectedArtwork(null);
    document.body.style.overflow = 'auto';
    setModalOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    setSelectedImageIndex(prevIndex => ({
      ...prevIndex,
      [selectedArtwork.id]: (prevIndex[selectedArtwork.id] + 1) % selectedArtwork.source.length
    }));
  }, [selectedArtwork]);

  const prevImage = useCallback(() => {
    setSelectedImageIndex(prevIndex => ({
      ...prevIndex,
      [selectedArtwork.id]:
        prevIndex[selectedArtwork.id] === 0 ? selectedArtwork.source.length - 1 : prevIndex[selectedArtwork.id] - 1
    }));
  }, [selectedArtwork]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeModal, nextImage, prevImage]);

  return (
    <Container selectedArtwork={selectedArtwork}>
      <ArtworksContainer maxColumns={maxColumns}>
        {artworks.map((artwork) => (
          <ArtworkCard
            key={artwork.title}
            onClick={() => openModal(artwork)}
            isSelected={selectedArtwork && selectedArtwork.id === artwork.id}
            isMobile={isMobile}
            portrait={artwork.aspectRatio <= 1}
          >
            {artwork.type === 'image' && (
              <ArtworkImage
                src={`/images/${artwork.source[0]}`}
                alt={artwork.title}
                portrait={artwork.aspectRatio <= 1}
              />
            )}
            {artwork.type === 'video' && (
              <VideoPlayer controls autoPlay loop muted>
                <source src={`/videos/${artwork.source}`} type="video/mp4" />
                Your browser does not support the video tag.
              </VideoPlayer>
            )}
          </ArtworkCard>
        ))}
      </ArtworksContainer>
      {selectedArtwork && (
        <ArtworkModal
        artwork={selectedArtwork}
        onClose={closeModal}
        selectedImageIndex={selectedImageIndex[selectedArtwork.id]}
        onNextImage={nextImage}
        onPrevImage={prevImage}
      />
      
      )}
    </Container>
  );
};

export default Artworks;
